
/**
 * Utility functions for audio playback throughout the application
 */

// Pre-cache the notification sound for better performance
let notificationSound: HTMLAudioElement | null = null;

// Initialize the audio on app load
export const initializeAudio = () => {
  try {
    notificationSound = new Audio('/notification-sound.mp3');
    notificationSound.preload = 'auto';
    // Attempt to load the audio file
    notificationSound.load();
    console.log('Notification sound initialized');
    return true;
  } catch (error) {
    console.error('Failed to initialize notification sound:', error);
    return false;
  }
};

/**
 * Play the completion sound with multiple fallback strategies
 * @returns Promise that resolves when sound plays or fails
 */
export const playCompletionSound = async (): Promise<boolean> => {
  console.log('Attempting to play completion sound');
  
  try {
    // First attempt: Create a fresh Audio instance to avoid autoplay restrictions
    const tempSound = new Audio('/notification-sound.mp3');
    tempSound.volume = 0.7;
    await tempSound.play();
    console.log('Completion sound played successfully (new instance)');
    return true;
  } catch (error) {
    console.warn('First attempt to play sound failed:', error);
    
    try {
      // Second attempt: Use the cached audio instance if available
      if (notificationSound) {
        notificationSound.currentTime = 0;
        await notificationSound.play();
        console.log('Completion sound played successfully (cached instance)');
        return true;
      }
    } catch (secondError) {
      console.error('All attempts to play sound failed:', secondError);
    }
    
    // Final attempt: Create a basic beep using Web Audio API
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.type = 'sine';
      oscillator.frequency.value = 800;
      gainNode.gain.value = 0.1;
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.start(0);
      setTimeout(() => oscillator.stop(), 200);
      
      console.log('Played fallback beep sound');
      return true;
    } catch (finalError) {
      console.error('Even fallback beep failed:', finalError);
      return false;
    }
  }
};

/**
 * Request notification permissions from the browser
 * @returns Promise resolving to the permission state
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return 'denied';
  }
  
  if (Notification.permission !== 'granted' && Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission;
  }
  
  return Notification.permission;
};

/**
 * Send a browser notification
 * @param title Notification title
 * @param options Notification options
 * @returns The notification object or null
 */
export const sendNotification = (title: string, options: NotificationOptions = {}): Notification | null => {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return null;
  }
  
  try {
    return new Notification(title, {
      icon: '/favicon.ico',
      ...options
    });
  } catch (error) {
    console.error('Failed to send notification:', error);
    return null;
  }
};

// Initialize audio when the module is loaded
if (typeof window !== 'undefined') {
  initializeAudio();
}
